import React, { useState } from "react";
import { ValidationRequest } from "../../../model_gen/finetuning";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../../ui/paginated-table";
import { Button } from "../../ui/button";
import { 
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import moment from "moment";

interface ValidationResultsTableProps {
  requests: ValidationRequest[];
  isLoading: boolean;
  onViewRequest: (request: ValidationRequest) => void;
}

const ValidationResultsTable: React.FC<ValidationResultsTableProps> = ({
  requests,
  isLoading,
  onViewRequest,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig<ValidationRequest>>({ 
    key: null, 
    direction: "desc" 
  });
  const pageSize = 10;

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const handleSort = (key: keyof ValidationRequest) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const columns: PaginatedTableColumn<ValidationRequest>[] = [
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[15%]",
      sortable: true,
      render: (request) => (
        request.date_created ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(request.date_created)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(request.date_created)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">Unknown</span>
        )
      ),
    },
    {
      key: "request_id",
      header: "Request ID",
      width: "w-[15%]",
      sortable: true,
      render: (request) => (
        <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
          {request.request_id}
        </code>
      ),
    },
    {
      key: "correct",
      header: "Status",
      width: "w-[10%]",
      sortable: true,
      render: (request) => (
        <div className="flex items-center gap-2">
          {request.correct ? (
            <CheckCircleIcon className="w-4 h-4 text-green-600" />
          ) : (
            <XCircleIcon className="w-4 h-4 text-red-600" />
          )}
          <span className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            request.correct 
              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          )}>
            {request.correct ? "Correct" : "Incorrect"}
          </span>
        </div>
      ),
    },
    {
      key: "expected_output",
      header: "Expected Output",
      width: "w-[25%]",
      render: (request) => (
        <div className="text-sm font-gc-reg text-foreground max-h-20 overflow-auto whitespace-pre-wrap">
          {request.expected_output || "-"}
        </div>
      ),
    },
    {
      key: "actual_output",
      header: "Actual Output",
      width: "w-[25%]",
      render: (request) => (
        <div className="text-sm font-gc-reg text-foreground max-h-20 overflow-auto whitespace-pre-wrap">
          {request.actual_output || "-"}
        </div>
      ),
    },
    {
      key: "request_id", // Using request_id as key for actions column
      header: "Actions",
      width: "w-[10%]",
      render: (request) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={(e) => {
            e.stopPropagation();
            onViewRequest(request);
          }}
        >
          <EyeIcon className="w-3 h-3" />
          Details
        </Button>
      ),
    },
  ];

  return (
    <PaginatedTable
      data={requests}
      columns={columns}
      loading={isLoading}
      sortConfig={sortConfig}
      onSort={handleSort}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      emptyTitle="No requests found"
      emptyDescription="No validation requests are available for this run."
      loadingRows={5}
    />
  );
};

export default ValidationResultsTable; 